'use client';

import { readAnalyticsConsentState } from '@/analytics/analytics-consent';
import { resolveGoogleAnalyticsMeasurementId } from '@/analytics/analytics-runtime-config';
import {
  applyGoogleAnalyticsConsent,
  setGoogleAnalyticsUserId,
} from '@/analytics/google-analytics-browser';
import { useAuthSession } from '@/lib/auth/auth-session-provider';
import { matchesRoutePrefix, stripLocalePrefix } from '@/lib/i18n/locale-path';
import { GoogleAnalytics } from '@next/third-parties/google';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';

function isAdminPath(pathname: string | null) {
  const normalizedPath = stripLocalePrefix(pathname ?? '/');
  return matchesRoutePrefix(normalizedPath, '/admin');
}

export function GoogleAnalyticsLoader() {
  const { data: session, isPending } = useAuthSession();
  const [shouldRender, setShouldRender] = useState(false);
  const pathname = usePathname();
  const gaMeasurementId = resolveGoogleAnalyticsMeasurementId();

  useEffect(() => {
    setShouldRender(true);
  }, []);

  useEffect(() => {
    if (!gaMeasurementId || isAdminPath(pathname)) {
      return;
    }

    applyGoogleAnalyticsConsent({
      analyticsStorage: readAnalyticsConsentState(),
      mode: 'default',
    });
  }, [pathname]);

  useEffect(() => {
    if (!gaMeasurementId || isAdminPath(pathname) || isPending) {
      return;
    }
    setGoogleAnalyticsUserId(session?.user.id ?? null);
  }, [gaMeasurementId, isPending, pathname, session?.user.id]);

  if (!gaMeasurementId || !shouldRender || isAdminPath(pathname)) {
    return null;
  }

  return <GoogleAnalytics gaId={gaMeasurementId} />;
}
